import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { observer } from 'mobx-react';
import { Box, Typography } from '@material-ui/core';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import { Fetcher } from '@this/src/util';
import { Button } from '@this/shared/ui/inputs/button';
import { Input } from '@this/shared/ui/inputs/input';
import { Alert } from '@this/shared/ui/feedbacks/alert';
import { Modal, ModalBody } from '@this/shared/ui/feedbacks/modal';
import { Loading } from '@this/shared/ui/feedbacks/loading';
import {
  OrganizationTable,
  OrganizationTh,
  OrganizationButtonTh,
  OrganizationTd,
  OrganizationTdButton,
  OrganizationBodyFooterActions
} from '@this/components/organization/organization.style';
import { DeleteMasterButton } from '@this/components/organization/delete-master-button';
import type InvoiceCustomFormat from '@this/domain/invoice_custom_format/invoice_custom_format';
import type InvoiceStandardTable from '@this/src/domain/invoice_custom_format/invoice_standard_table';
import InvoiceCustomFormatField from '@this/src/domain/invoice_custom_format/invoice_custom_format_field';
import InvoiceCustomFormatFieldForm from './invoice_custom_format_field_form';

interface Props {
  invoiceCustomFormat: InvoiceCustomFormat;
  invoiceStandardTables: InvoiceStandardTable[];
  onSubmit: () => void;
  saving?: boolean;
}

interface SectionHeadingProps {
  children: React.ReactNode;
  actions?: React.ReactNode;
}

const SectionHeading: React.FC<SectionHeadingProps> = ({ children, actions }) => {
  return (
    <Box borderBottom="2px solid #f4f5f7" paddingBottom="10px" marginBottom="15px">
      <Box display="flex" alignItems="center" justifyContent={actions ? 'space-between' : 'flex-start'}>
        <Typography component="h2" style={{ fontWeight: 'bold', margin: 0, color: '#444', fontSize: '16px' }}>
          {children}
        </Typography>
        {actions}
      </Box>
    </Box>
  );
};

const InvoiceCustomFormatForm = observer(
  ({ invoiceCustomFormat, invoiceStandardTables, onSubmit, saving = false }: Props) => {
    const [editingField, setEditingField] = useState<InvoiceCustomFormatField | null>(null);
    const [editTargetField, setEditTargetField] = useState<InvoiceCustomFormatField | null>(null);
    const [errors, setErrors] = useState<string[]>([]);
    const [fetchingPreviews, setFetchingPreviews] = useState(false);

    const fetchFieldPreviews = async () => {
      const params = invoiceCustomFormat.invoiceCustomFormatFields.map(field => {
        return field.submitParams();
      });
      if (params.length === 0) return;
      setFetchingPreviews(true);
      const response = await Fetcher.get<{ results: { [key: string]: string } }>(
        `/organization/invoice_custom_formats/field_previews.json`,
        { field_params: params }
      );
      const results = response.results;
      Object.keys(results).forEach(key => {
        const field = invoiceCustomFormat.invoiceCustomFormatFields.find(f => f.name === key);
        if (field === undefined) return;
        field.preview = results[key];
      });
      setFetchingPreviews(false);
    };

    useEffect(() => {
      fetchFieldPreviews();
    }, []);

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setErrors([]);
      const errs = invoiceCustomFormat.validate();
      setErrors(errs);
      if (errs.length > 0) return;
      onSubmit();
    };

    const handleAddField = () => {
      const field = new InvoiceCustomFormatField({
        column_type: 'standard'
      });
      setEditingField(field);
      setEditTargetField(null);
    };

    const handleEditField = (field: InvoiceCustomFormatField) => {
      const fieldCopy = _.cloneDeep(field);
      setEditingField(fieldCopy);
      setEditTargetField(field);
    };

    const handleConfirmField = () => {
      if (editingField === null) return;
      if (editTargetField === null) {
        invoiceCustomFormat.invoiceCustomFormatFields.push(editingField);
      } else {
        const index = invoiceCustomFormat.invoiceCustomFormatFields.findIndex(f => f === editTargetField);
        if (index === -1) return;
        invoiceCustomFormat.invoiceCustomFormatFields[index] = editingField;
      }
      setEditingField(null);
      setEditTargetField(null);
      fetchFieldPreviews();
    };

    const handleCancelField = () => {
      setEditingField(null);
      setEditTargetField(null);
    };

    const handleRemoveField = async (field: InvoiceCustomFormatField) => {
      field.removed = true;
      if (!_.isNil(invoiceCustomFormat.id) && !_.isNil(field.id)) {
        await Fetcher.delete(
          `/organization/invoice_custom_formats/${invoiceCustomFormat.id}/invoice_custom_format_fields/${field.id}.json`
        );
      }
      invoiceCustomFormat.invoiceCustomFormatFields = invoiceCustomFormat.invoiceCustomFormatFields.filter(
        f => !f.removed
      );
      fetchFieldPreviews();
    };

    return (
      <>
        <form onSubmit={handleSubmit}>
          <Box marginBottom="40px">
            <SectionHeading>基本設定</SectionHeading>
            <Box display="flex" alignItems="center">
              <Box width="80px" component="label">
                <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                  名称
                </Typography>
              </Box>
              <Box width="250px">
                <Input
                  id="name"
                  type="text"
                  value={invoiceCustomFormat.name}
                  onChange={e => {
                    invoiceCustomFormat.name = e.target.value;
                  }}
                />
              </Box>
            </Box>
          </Box>
          {errors.length > 0 && (
            <Box marginBottom="16px">
              <Alert variant="danger" onClose={() => setErrors([])}>
                {errors.map(error => (
                  <Typography key={error} component="div">
                    {error}
                  </Typography>
                ))}
              </Alert>
            </Box>
          )}
          <Box marginBottom="40px">
            <SectionHeading
              actions={
                <Button color="sub" onClick={handleAddField}>
                  <Box component="span" marginRight="5px">
                    新しいカラムを追加
                  </Box>
                  <AddCircleOutlineIcon />
                </Button>
              }
            >
              出力カラム設定
            </SectionHeading>
            <OrganizationTable>
              <thead>
                <tr>
                  <OrganizationTh>カラム名</OrganizationTh>
                  <OrganizationButtonTh>編集</OrganizationButtonTh>
                  <OrganizationButtonTh>削除</OrganizationButtonTh>
                </tr>
              </thead>
              <tbody>
                {invoiceCustomFormat.invoiceCustomFormatFields.map(field => (
                  <tr key={field.id}>
                    <OrganizationTd>{field.name}</OrganizationTd>
                    <OrganizationTd>
                      <Box textAlign="center">
                        <OrganizationTdButton onClick={() => handleEditField(field)}>編集</OrganizationTdButton>
                      </Box>
                    </OrganizationTd>
                    <OrganizationTd>
                      <Box textAlign="center">
                        <DeleteMasterButton targetLabel={field.name} onConfirm={() => handleRemoveField(field)} />
                      </Box>
                    </OrganizationTd>
                  </tr>
                ))}
              </tbody>
            </OrganizationTable>
          </Box>
          <Box marginBottom="40px">
            <SectionHeading>CSV出力プレビュー</SectionHeading>
            <Box style={{ overflowX: 'auto' }}>
              {fetchingPreviews ? (
                <Loading size="small" />
              ) : (
                <OrganizationTable>
                  <thead>
                    <tr>
                      {invoiceCustomFormat.invoiceCustomFormatFields.map((field, i) => (
                        <OrganizationTh key={i}>
                          <Box whiteSpace="nowrap">{field.name}</Box>
                        </OrganizationTh>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      {invoiceCustomFormat.invoiceCustomFormatFields.map((field, i) => (
                        <OrganizationTd key={i}>
                          <Box whiteSpace="nowrap">{field.preview}</Box>
                        </OrganizationTd>
                      ))}
                    </tr>
                  </tbody>
                </OrganizationTable>
              )}
            </Box>
          </Box>
          <OrganizationBodyFooterActions>
            <Box display="flex" gridGap="15px">
              <Button type="reset" color="sub" href="/organization/invoice_custom_formats" disabled={saving}>
                キャンセル
              </Button>
              <Button type="submit" color="primary" disabled={saving} loading={saving}>
                保存
              </Button>
            </Box>
          </OrganizationBodyFooterActions>
        </form>
        {editingField !== null && (
          <Modal size="large" open={editingField !== null} onClose={handleCancelField}>
            <ModalBody>
              <InvoiceCustomFormatFieldForm
                invoiceCustomFormatField={editingField}
                invoiceStandardTables={invoiceStandardTables}
                onCancel={handleCancelField}
                onConfirm={handleConfirmField}
              />
            </ModalBody>
          </Modal>
        )}
      </>
    );
  }
);

export default InvoiceCustomFormatForm;
